import { type ComponentProps, type ComponentType, useCallback, useEffect, useState } from 'react';
import { safeLocalStorage } from '@toss/storage';
import { BoltFillIcon, BoltIcon, Button, CheckSmallIcon, ChevronDownIcon, Dropdown, DropdownItem, GraduationcapFillIcon, GraduationcapIcon, Tooltip, XIcon } from '@zep-us/zus';
import { useTranslation } from 'next-i18next';
import { PlanBadge } from '@/shared/components/Badge/PlanBadge';
import { cn } from '@/utils';
import { type GenerationMode } from '../types';
type IconComponent = ComponentType<ComponentProps<typeof BoltIcon>>;
type SplitButtonOption = {
  mode: GenerationMode;
  /** 메인 버튼 leftSlot 용 Fill variant. Figma 컴포넌트 명 일치 (BoltFill, GraduationcapFill) */
  ButtonIcon: IconComponent;
  /** Dropdown row 용 outline variant. Figma 컴포넌트 명 일치 (Bolt, Graduationcap) */
  DropdownIcon: IconComponent;
};

// Figma: 메인 버튼은 Fill 아이콘(md=24px), dropdown row 는 outline 아이콘(sm=20px) 사용.
const OPTIONS: SplitButtonOption[] = [{
  mode: 'fast',
  ButtonIcon: BoltFillIcon,
  DropdownIcon: BoltIcon
}, {
  mode: 'thorough',
  ButtonIcon: GraduationcapFillIcon,
  DropdownIcon: GraduationcapIcon
}];

/**
 * - gradient: 최초 생성 CTA (violet→purple gradient + white text). Figma 36506-29126
 * - alternative: 재생성 CTA (light violet bg + violet text). Figma 36615-28445
 */
export type SplitButtonVariant = 'gradient' | 'alternative';
const VARIANT_STYLES: Record<SplitButtonVariant, {
  wrapper: string[];
  chevronTint: string;
}> = {
  gradient: {
    wrapper: ['text-white', 'bg-gradient-to-r from-violet-500 to-purple-400', 'hover:from-violet-600 hover:to-purple-500', 'data-[disabled]:from-violet-300 data-[disabled]:to-purple-200 data-[disabled]:opacity-[.54]'],
    chevronTint: 'bg-opacity-black-4 bg-blend-multiply'
  },
  alternative: {
    wrapper: ['text-strong', 'bg-violet-50 hover:bg-violet-100', 'data-[disabled]:opacity-[.54]'],
    chevronTint: 'bg-opacity-violet-2 group-hover:bg-opacity-violet-3'
  }
};
const COACH_MARK_DISMISSED_KEY = 'quiz.buildAi.generationMode.coachMarkDismissed';
const readDismissed = (): boolean => safeLocalStorage.get(COACH_MARK_DISMISSED_KEY) === '1';
const persistDismissed = () => {
  safeLocalStorage.set(COACH_MARK_DISMISSED_KEY, '1');
};
export type BuildAiGenerateSplitButtonProps = {
  selectedMode: GenerationMode;
  onModeChange: (mode: GenerationMode) => void;
  onGenerate: () => void;
  disabled?: boolean;
  /** 색상 톤. default 'gradient'(최초 생성). regenerate 흐름에서는 'alternative'. */
  variant?: SplitButtonVariant;
  /** Plan 권한으로 잠긴 모드. 해당 dropdown row 에 PlanBadge(basic) 가 노출된다. */
  lockedModes?: readonly GenerationMode[];
  /** 재생성 중일 때 메인 버튼 rightSlot 에 스피너 노출. */
  isLoading?: boolean;
  /**
   * Coach-mark 툴팁 노출 조건. 부모가 "프롬프트에 텍스트 입력됨" 같은 노출 트리거를 판단해서 전달한다.
   * 사용자가 chevron(dropdown) 을 열거나 닫기 X 를 누르면 영구 dismiss 되어 이후 prop 이 true 여도 노출되지 않는다.
   */
  shouldShowTooltip?: boolean;
  className?: string;
};
const LoadingSpinner = () => <svg className="size-5 animate-spin" viewBox="0 0 24 24" fill="none" aria-hidden data-sentry-element="svg" data-sentry-component="LoadingSpinner" data-sentry-source-file="BuildAiGenerateSplitButton.tsx">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" data-sentry-element="circle" data-sentry-source-file="BuildAiGenerateSplitButton.tsx" />
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" data-sentry-element="path" data-sentry-source-file="BuildAiGenerateSplitButton.tsx" />
  </svg>;

/**
 * 빠르게/꼼꼼하게 모드 split button.
 *
 * Coach-mark Tooltip (Figma 36675-30505):
 * - anchor: 오른쪽 chevron `<span>` (dropdown trigger 부분)
 * - 노출: shouldShowTooltip === true && !isDismissed
 * - dismiss: chevron 클릭(=Dropdown open) 또는 툴팁 X 클릭. localStorage 에 영구 저장.
 *
 * Figma: 36506-29126 (gradient), 36615-28445 (alternative), 36675-30505 (coach mark)
 */
export const BuildAiGenerateSplitButton = ({
  selectedMode,
  onModeChange,
  onGenerate,
  disabled = false,
  variant = 'gradient',
  lockedModes,
  isLoading = false,
  shouldShowTooltip = false,
  className
}: BuildAiGenerateSplitButtonProps) => {
  const {
    t
  } = useTranslation(['build']);

  // SSR/CSR 일치를 위해 첫 mount 후에만 localStorage 읽기. 첫 paint 는 항상 미dismiss 로 시작.
  const [isDismissed, setIsDismissed] = useState(false);
  useEffect(() => {
    if (readDismissed()) setIsDismissed(true);
  }, []);
  const dismiss = useCallback(() => {
    setIsDismissed(true);
    persistDismissed();
  }, []);
  const getModeLabel = (mode: GenerationMode): string => mode === 'fast' ? t('build:build.ai.generate.mode.fast') : t('build:build.ai.generate.mode.thorough');
  const currentOption = OPTIONS.find(option => option.mode === selectedMode) ?? OPTIONS[0];
  const {
    ButtonIcon
  } = currentOption;
  const variantStyles = VARIANT_STYLES[variant];
  const isTooltipOpen = shouldShowTooltip && !isDismissed;
  return <Dropdown.Root onOpenChange={open => {
    // chevron 클릭으로 dropdown 이 열리는 시점 = coach mark 영구 dismiss.
    if (open) dismiss();
  }} data-sentry-element="unknown" data-sentry-component="BuildAiGenerateSplitButton" data-sentry-source-file="BuildAiGenerateSplitButton.tsx">
      <Dropdown.Trigger asChild disabled={disabled} data-sentry-element="unknown" data-sentry-source-file="BuildAiGenerateSplitButton.tsx">
        <div role="button" aria-label={t('build:build.ai.generate.mode.menu')} className={cn('group inline-flex min-w-[230px] items-center overflow-hidden rounded-[8px] outline-none', variantStyles.wrapper, className)}>
          <Button variant="transparent" intent={variant} size="lg" disabled={disabled} onClick={onGenerate}
        // wrapper(=Trigger) 로 bubbling 되어 dropdown 이 열리는 것을 막는다.
        onPointerDown={e => e.stopPropagation()} leftSlot={isLoading ? <LoadingSpinner /> : <ButtonIcon size="sm" />} className="grow" data-sentry-element="Button" data-sentry-source-file="BuildAiGenerateSplitButton.tsx">
            {getModeLabel(currentOption.mode)}
          </Button>
          <Tooltip.Provider data-sentry-element="unknown" data-sentry-source-file="BuildAiGenerateSplitButton.tsx">
            <Tooltip.Root open={isTooltipOpen} data-sentry-element="unknown" data-sentry-source-file="BuildAiGenerateSplitButton.tsx">
              <Tooltip.Trigger asChild data-sentry-element="unknown" data-sentry-source-file="BuildAiGenerateSplitButton.tsx">
                <span aria-hidden className={cn('flex h-[48px] w-[40px] items-center justify-center', variantStyles.chevronTint)}>
                  {/* 메뉴 open 시 chevron 180° flip (group = wrapper Trigger 의 data-state 추적) */}
                  <ChevronDownIcon size="sm" className="transition-transform group-data-[state=open]:rotate-180" data-sentry-element="ChevronDownIcon" data-sentry-source-file="BuildAiGenerateSplitButton.tsx" />
                </span>
              </Tooltip.Trigger>
              <Tooltip.Content side="top" align="center" sideOffset={8} className="flex max-w-[300px] items-center gap-2xs" data-sentry-element="unknown" data-sentry-source-file="BuildAiGenerateSplitButton.tsx">
                {/* 기본 TooltipContent 스타일(bg-opacity-black-9 + text-white)을 그대로 사용. */}
                <Tooltip.Arrow data-sentry-element="unknown" data-sentry-source-file="BuildAiGenerateSplitButton.tsx" />
                <span>{t('build:build.ai.generate.mode.coachMark')}</span>
                <button type="button"
              // wrapper(Dropdown.Trigger) 로 bubble 되어 dropdown 이 열리지 않도록 둘 다 차단.
              onPointerDown={e => e.stopPropagation()} onClick={e => {
                e.stopPropagation();
                dismiss();
              }} aria-label={t('build:build.ai.generate.mode.coachMark.close')} className="text-white opacity-70 hover:opacity-100">
                  <XIcon size="xs" data-sentry-element="XIcon" data-sentry-source-file="BuildAiGenerateSplitButton.tsx" />
                </button>
              </Tooltip.Content>
            </Tooltip.Root>
          </Tooltip.Provider>
        </div>
      </Dropdown.Trigger>

      <Dropdown.Portal data-sentry-element="unknown" data-sentry-source-file="BuildAiGenerateSplitButton.tsx">
        <Dropdown.Content align="end" side="top" sideOffset={4} data-sentry-element="unknown" data-sentry-source-file="BuildAiGenerateSplitButton.tsx">
          {OPTIONS.map(option => {
          const isChecked = option.mode === selectedMode;
          const isLocked = lockedModes?.includes(option.mode) ?? false;
          const {
            DropdownIcon
          } = option;
          return <DropdownItem key={option.mode} checked={isChecked} onClick={() => onModeChange(option.mode)} className="gap-2xs">
                <DropdownIcon size="sm" />
                <span className="flex-1 text-left">
                  {getModeLabel(option.mode)}
                </span>
                {/*
                 * locked 상태가 우선 (PlanBadge). 그렇지 않을 때 selected 의 체크 표시.
                 * BaseDropdownItemUI checked state 가 text-strong 을 상속시켜 줌.
                 */}
                {isLocked ? <PlanBadge planType="BASIC" tooltip={false} /> : isChecked && <CheckSmallIcon size="sm" />}
              </DropdownItem>;
        })}
        </Dropdown.Content>
      </Dropdown.Portal>
    </Dropdown.Root>;
};